import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import type {
  InboxMessage,
  PushNotificationConfigResponse,
  PushNotificationPreferencesResponse,
  PushSubscriptionInput,
  PushSubscriptionResponse,
} from "../shared/contracts";
import { apiRequest } from "./api";

export type MailNotificationPermission = NotificationPermission | "unsupported";
export type BackgroundNotificationStatus =
  | "checking"
  | "idle"
  | "subscribing"
  | "enabled"
  | "manual"
  | "unsupported"
  | "error";

const EDGE_USER_AGENT = /Edg(?:A|iOS)?\//;
const PUSH_TIMEOUT_MS = 10_000;

function currentNotificationPermission(): MailNotificationPermission {
  return "Notification" in window ? Notification.permission : "unsupported";
}

function supportsBackgroundNotifications(): boolean {
  return window.isSecureContext
    && "Notification" in window
    && "serviceWorker" in navigator
    && "PushManager" in window;
}

function withTimeout<T>(promise: Promise<T>, milliseconds: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error("notification_timeout")), milliseconds);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timer);
        reject(error);
      },
    );
  });
}

function urlBase64ToArrayBuffer(value: string): ArrayBuffer {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const decoded = window.atob((value + padding).replaceAll("-", "+").replaceAll("_", "/"));
  const bytes = new Uint8Array(decoded.length);
  for (let index = 0; index < decoded.length; index += 1) bytes[index] = decoded.charCodeAt(index);
  return bytes.buffer;
}

function arrayBufferToBase64Url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return window.btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

function serializeSubscription(subscription: PushSubscription): PushSubscriptionInput {
  const p256dh = subscription.getKey("p256dh");
  const auth = subscription.getKey("auth");
  if (!p256dh || !auth) throw new Error("push_keys_unavailable");
  return {
    endpoint: subscription.endpoint,
    expirationTime: subscription.expirationTime,
    keys: {
      p256dh: arrayBufferToBase64Url(p256dh),
      auth: arrayBufferToBase64Url(auth),
    },
  };
}

function messageNotificationUrl(tenantId: string, mailboxId: string, messageId: string): string {
  const search = new URLSearchParams({ tenantId, mailboxId, messageId });
  return `/caixa-de-entrada?${search.toString()}`;
}

async function showPowerMailNotification(title: string, body: string, tag: string, url = "/") {
  const options: NotificationOptions = {
    body,
    icon: "/pwa-192x192.png",
    badge: "/pwa-64x64.png",
    tag,
    data: { url },
  };
  if ("serviceWorker" in navigator) {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration) {
      await registration.showNotification(title, options);
      return;
    }
  }
  const notification = new Notification(title, options);
  notification.onclick = () => {
    if (url !== "/") window.location.assign(url);
    window.focus();
    notification.close();
  };
}

export function useMailNotifications(
  messages: InboxMessage[],
  tenantId: string | undefined,
  mailboxKey: string | undefined,
  inboxReady: boolean,
  accountKey: string | undefined,
) {
  const isEdge = EDGE_USER_AGENT.test(navigator.userAgent);
  const [permission, setPermission] = useState<MailNotificationPermission>(currentNotificationPermission);
  const [backgroundStatus, setBackgroundStatus] = useState<BackgroundNotificationStatus>("checking");
  const [backgroundError, setBackgroundError] = useState<string | null>(null);
  const [selectedMailboxIds, setSelectedMailboxIds] = useState<string[]>([]);
  const [preferencesLoading, setPreferencesLoading] = useState(false);
  const syncAttemptRef = useRef(0);
  const knownMessagesRef = useRef<{ mailboxKey: string | undefined; initialized: boolean; ids: Set<string> }>({
    mailboxKey,
    initialized: false,
    ids: new Set(),
  });

  const synchronizeBackgroundSubscription = useCallback(async () => {
    const config = await apiRequest<PushNotificationConfigResponse>("/api/v1/notifications/push-config");
    if (!config.enabled || !config.publicKey) throw new Error("push_not_configured");

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToArrayBuffer(config.publicKey),
      });
    }
    await apiRequest<PushSubscriptionResponse>("/api/v1/notifications/subscriptions", {
      method: "POST",
      body: JSON.stringify(serializeSubscription(subscription)),
    });
  }, []);

  const loadMailboxPreferences = useCallback(async () => {
    const response = await apiRequest<PushNotificationPreferencesResponse>("/api/v1/notifications/preferences");
    setSelectedMailboxIds(response.mailboxIds);
    return response;
  }, []);

  const saveMailboxPreferences = useCallback(async (mailboxIds: string[]) => {
    setPreferencesLoading(true);
    try {
      const response = await apiRequest<PushNotificationPreferencesResponse>("/api/v1/notifications/preferences", {
        method: "PUT",
        body: JSON.stringify({ mailboxIds }),
      });
      setSelectedMailboxIds(response.mailboxIds);
      return response;
    } finally {
      setPreferencesLoading(false);
    }
  }, []);

  const registerInBackground = useCallback(async (nextStatus: "checking" | "subscribing") => {
    const attempt = syncAttemptRef.current + 1;
    syncAttemptRef.current = attempt;
    setBackgroundStatus(nextStatus);
    setBackgroundError(null);
    try {
      await withTimeout(synchronizeBackgroundSubscription(), PUSH_TIMEOUT_MS);
      if (syncAttemptRef.current !== attempt) return "enabled" as const;
      setBackgroundStatus("enabled");
      return "enabled" as const;
    } catch (error) {
      if (syncAttemptRef.current !== attempt) return isEdge ? "manual" as const : "error" as const;
      const message = error instanceof Error ? error.message : "notification_setup_failed";
      setBackgroundError(message);
      const failedStatus = isEdge ? "manual" : "error";
      setBackgroundStatus(failedStatus);
      return failedStatus;
    }
  }, [isEdge, synchronizeBackgroundSubscription]);

  useEffect(() => {
    const refreshPermission = () => {
      const nextPermission = currentNotificationPermission();
      setPermission(nextPermission);
      if (nextPermission === "granted" && backgroundStatus !== "enabled" && backgroundStatus !== "subscribing") {
        void registerInBackground("checking");
      }
    };
    window.addEventListener("focus", refreshPermission);
    return () => window.removeEventListener("focus", refreshPermission);
  }, [backgroundStatus, registerInBackground]);

  useEffect(() => {
    if (!accountKey) return;
    let active = true;
    setPreferencesLoading(true);
    void loadMailboxPreferences()
      .catch(() => undefined)
      .finally(() => { if (active) setPreferencesLoading(false); });
    return () => { active = false; };
  }, [accountKey, loadMailboxPreferences]);

  useEffect(() => {
    if (!accountKey) return;
    if (!supportsBackgroundNotifications()) {
      setPermission(currentNotificationPermission());
      setBackgroundStatus("unsupported");
      return;
    }

    const nextPermission = currentNotificationPermission();
    setPermission(nextPermission);
    if (nextPermission === "granted") {
      void registerInBackground("checking").then((result) => {
        if (result === "enabled") void loadMailboxPreferences().catch(() => undefined);
      });
    }
    else setBackgroundStatus(nextPermission === "denied" ? "manual" : "idle");
  }, [accountKey, loadMailboxPreferences, registerInBackground]);

  useEffect(() => {
    const tracker = knownMessagesRef.current;
    if (tracker.mailboxKey !== mailboxKey) {
      knownMessagesRef.current = { mailboxKey, initialized: false, ids: new Set() };
    }
  }, [mailboxKey]);

  useEffect(() => {
    if (!tenantId || !mailboxKey || !inboxReady) return;
    const tracker = knownMessagesRef.current;
    const inboundMessages = messages.filter(
      (message) => message.direction === "inbound" && message.folder === "inbox",
    );
    if (!tracker.initialized) {
      tracker.ids = new Set(messages.map((message) => message.id));
      tracker.initialized = true;
      return;
    }
    const arrivals = inboundMessages.filter((message) => !tracker.ids.has(message.id));
    messages.forEach((message) => tracker.ids.add(message.id));
    if (
      backgroundStatus === "enabled"
      || permission !== "granted"
      || arrivals.length === 0
      || !selectedMailboxIds.includes(mailboxKey)
    ) return;
    const newest = arrivals[0];
    const title = arrivals.length === 1
      ? `Novo e-mail de ${newest.sender.name}`
      : `${arrivals.length} novos e-mails no PowerMail`;
    const body = arrivals.length === 1 ? newest.subject : `O mais recente: ${newest.subject}`;
    void showPowerMailNotification(
      title,
      body,
      `powermail-inbox-${newest.id}`,
      messageNotificationUrl(tenantId, mailboxKey, newest.id),
    ).catch(() => undefined);
  }, [backgroundStatus, inboxReady, mailboxKey, messages, permission, selectedMailboxIds, tenantId]);

  const activate = useCallback(async (): Promise<BackgroundNotificationStatus> => {
    if (!supportsBackgroundNotifications()) {
      setPermission(currentNotificationPermission());
      setBackgroundStatus("unsupported");
      return "unsupported";
    }
    if (Notification.permission === "denied") {
      setPermission("denied");
      setBackgroundStatus("manual");
      return "manual";
    }

    setBackgroundStatus("subscribing");
    setBackgroundError(null);
    try {
      const nextPermission = Notification.permission === "granted"
        ? "granted"
        : await withTimeout(Notification.requestPermission(), PUSH_TIMEOUT_MS);
      setPermission(nextPermission);
      if (nextPermission !== "granted") {
        setBackgroundStatus(isEdge ? "manual" : "idle");
        return isEdge ? "manual" : "idle";
      }
      const result = await registerInBackground("subscribing");
      if (result === "enabled") {
        void loadMailboxPreferences().catch(() => undefined);
        void showPowerMailNotification(
          "Notificações ativadas",
          "A Bia vai avisar mesmo quando o PowerMail estiver fechado.",
          "powermail-notifications-enabled",
        ).catch(() => undefined);
      }
      return result;
    } catch (error) {
      setBackgroundError(error instanceof Error ? error.message : "notification_setup_failed");
      const failedStatus = isEdge ? "manual" : "error";
      setBackgroundStatus(failedStatus);
      return failedStatus;
    }
  }, [isEdge, loadMailboxPreferences, registerInBackground]);

  return {
    activate,
    backgroundError,
    backgroundStatus,
    isEdge,
    permission,
    preferencesLoading,
    saveMailboxPreferences,
    selectedMailboxIds,
  };
}
